<template>
  <div ref="crudTableFitRoot">
    <v-data-table
      :headers="headers"
      :items="solarExamineReports"
      class="elevation-1"
      hide-default-footer
      :loading="apiLoading"
      :custom-sort="customSort"
      :loading-text="$t('fileDetail.loadingData')"
      :data-testid="getTestId('report-data-table')"
      :options.sync="tableOptions"
      @update:options="changedOptions"
    >
      <template slot="top">
        <v-toolbar
          flat
          class="solvit-crud-table-toolbar"
          :data-testid="getTestId('report-toolbar')"
        >
          <v-toolbar-title style="align-items: center; display: flex">
            <v-icon left> mdi-calendar-search-outline </v-icon
            >{{ $t('inspection.reportCrudToolbarTitle') }}
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('inspection.searchReports')"
            single-line
            hide-details
            :data-testid="getTestId('report-search-input')"
          />
          <v-spacer />
          <v-btn
            outlined
            style="font-weight: 400; gap: 10px; display: flex"
            class="mb-2 solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            :data-testid="getTestId('report-add-button')"
            @click="openDialog(null, 'add')"
          >
            <v-icon size="medium"> mdi-plus </v-icon>
            {{ $t('common.addNew') }}
          </v-btn>
        </v-toolbar>
      </template>
      <template slot="item.name" slot-scope="{ item }">
        <div class="d-flex align-center flex-wrap" style="gap: 6px; min-width: 0">
          <span class="text-truncate">{{ item.name }}</span>
          <v-chip
            v-if="isReportRowWorkInProgress(item)"
            x-small
            label
            color="primary"
            outlined
            class="solvit-row-work-badge flex-shrink-0"
          >
            {{ $t('common.workInProgress') }}
          </v-chip>
        </div>
      </template>
      <template slot="item.uploadedImageCounts" slot-scope="{ item }">
        <v-tooltip bottom :disabled="!hasUploadCountMismatch(item)">
          <template v-slot:activator="{ on, attrs }">
            <span
              v-bind="attrs"
              :class="uploadedImageCountsClass(item)"
              class="text-no-wrap"
              :data-testid="getTestId(`uploaded-image-counts-${item.id}`)"
              v-on="on"
            >
              {{ formatUploadedImageCounts(item) }}
            </span>
          </template>
          <span>{{ $t('inspection.uploadedImageCountMismatchHint') }}</span>
        </v-tooltip>
      </template>
      <template slot="item.fileUpload" slot-scope="{ item }">
        <v-btn
          small
          outlined
          :class="[
            'solvit-crud-outline-btn',
            { 'solvit-crud-outline-btn--progress-active': isUploadLocked(item) }
          ]"
          :data-testid="getTestId(`file-upload-button-${item.id}`)"
          :disabled="item.fileUpload"
          @click="openUploadDialog(item, 'upload')"
        >
          <v-icon left> mdi-upload </v-icon>{{ $t('common.upload') }}
        </v-btn>
      </template>
      <template slot="item.imagePairing" slot-scope="{ item }">
        <v-text
          v-if="pairingStatus(item.id) === PairingStatus.pairing"
          color="secondary"
          :data-testid="getTestId(`pairing-loading-${item.id}`)"
          @click="startPairing(item)"
        >
          <v-progress-circular indeterminate width="4" size="25" color="secondary" />
        </v-text>
        <v-text
          v-else-if="pairingStatus(item.id) === PairingStatus.error"
          color="secondary"
          :data-testid="getTestId(`pairing-error-${item.id}`)"
          @click="startPairing(item)"
        >
          <v-progress-circular color="error" value="100" width="4" size="25" />
        </v-text>
        <v-btn
          v-else
          small
          outlined
          style="cursor: pointer"
          class="solvit-crud-outline-btn"
          :data-testid="getTestId(`pairing-start-button-${item.id}`)"
          :disabled="pairingStatus(item.id) === PairingStatus.pairing"
          @click="startPairing(item)"
        >
          <v-icon left> mdi-link </v-icon>{{ $t('pairing.title') }}
        </v-btn>
      </template>
      <template slot="item.defectAdd" slot-scope="{ item }">
        <v-btn
          small
          outlined
          :class="[
            'solvit-crud-outline-btn',
            { 'solvit-crud-outline-btn--progress-active': isDefectLocked(item) }
          ]"
          :data-testid="getTestId(`defect-add-button-${item.id}`)"
          @click="openUploadDialog(item, 'defect')"
          :disabled="item.defectAdd"
        >
          <v-icon left> mdi-plus-box </v-icon>{{ $t('defects.addDefects') }}
        </v-btn>
      </template>
      <template slot="item.aIAnalyze" slot-scope="{ item }">
        <v-btn
          small
          outlined
          :class="[
            'center-content solvit-crud-outline-btn',
            { 'solvit-crud-outline-btn--progress-active': isAiRunning(item) }
          ]"
          :data-testid="getTestId(`ai-work-button-${item.id}`)"
          @click="openAIAnalyzeDialog(item)"
        >
          <v-icon left>
            {{ aiAnalyzeButtonIcon(item) }}
          </v-icon>
          {{ aiAnalyzeButtonLabel(item) }}
        </v-btn>
      </template>
      <template slot="item.fileDetail" slot-scope="{ item }">
        <v-btn
          small
          outlined
          :class="[
            'solvit-crud-outline-btn',
            { 'solvit-crud-outline-btn--progress-active': isDefectLocked(item) }
          ]"
          :data-testid="getTestId(`file-detail-button-${item.id}`)"
          :disabled="item.defectAdd"
          @click="openFileDialog(item, 'detail')"
        >
          <v-icon left> mdi-file-search-outline </v-icon>{{ $t('inspection.tableFileDetail') }}
        </v-btn>
      </template>
      <template slot="item.actions" slot-scope="{ item }">
        <FixDeleteBtn
          :key="item.id"
          :item="item"
          :data-testid="getTestId(`action-buttons-${item.id}`)"
          @editItem="editItem"
          @deleteItem="deleteItem"
        />
      </template>
      <template slot="no-data">
        <v-btn
          outlined
          class="solvit-crud-outline-btn"
          :data-testid="getTestId('refresh-button')"
          @click="initialize"
        >
          {{ $t('fileDetail.refresh') }}
        </v-btn>
      </template>
    </v-data-table>
    <UploadDialog
      :key="currentReportId + dialogMode"
      :dialog="uploadDialog"
      :edited-item="editedItem"
      :sites="sites"
      :examine-hours="examineHours"
      :report-id="currentReportId ? currentReportId : 0"
      :mode="mode"
      :test-id="getTestId('upload-dialog')"
      @close="closeUploadDialog"
      @keydown.esc="closeUploadDialog"
      @saved="id => onSave(id)"
      @initialize="onInitialize"
      @error="onError"
      @update:dialog="uploadDialog = $event"
    />
    <AIAnalyzeDialog
      :key="currentReportId + 'aiAnalyze'"
      :dialog="aIAnalyzeDialog"
      :edited-item="editedItem"
      :report-id="currentReportId ? currentReportId : 0"
      :data-testid="getTestId('ai-analyze-dialog')"
      @close="closeAIAnalyzeDialog"
      @keydown.esc="closeAIAnalyzeDialog"
      persistent
      no-click-animation
      @initialize="onInitialize"
      @fine="onFine"
      @update:dialog="aIAnalyzeDialog = $event"
    />
    <UploadWrapper
      :key="currentReportId ? `report-${currentReportId}-${mode}` : `new-${mode}`"
      :dialog="dialog"
      :edited-item="editedItem"
      :sites="sites"
      :examine-hours="examineHours"
      :report-id="currentReportId ? currentReportId : 0"
      :mode="mode"
      @close="closeDialog"
      @keydown.esc="closeUploadDialog"
      @saved="id => onSave(id)"
      @initialize="onInitialize"
      @error="onError"
      @update:dialog="dialog = $event"
    />
    <FileDetailDialog
      :key="currentReportId ? `file-${currentReportId}-${mode}` : `new-file-${mode}`"
      :dialog="fileDetailDialog"
      :edited-item="editedItem"
      :sites="sites"
      :examine-hours="examineHours"
      :report-id="currentReportId ? currentReportId : 0"
      :mode="mode"
      :data-testid="getTestId('file-detail-dialog')"
      @close="closeFileDialog"
      @keydown.esc="closeFileDialog"
      @saved="id => onSave(id)"
      @initialize="onInitialize"
      @error="onError"
      @update:dialog="fileDetailDialog = $event"
    />
    <div class="text-center pt-2" :data-testid="getTestId('pagination-container')">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="10"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        :data-testid="getTestId('pagination')"
      />
    </div>
  </div>
</template>

<script>
  import Api from '../../apis/Api';
  import axios from 'axios';
  import { authHeader } from '../../_helpers';

  const _appDomain = process.env.VUE_APP_DOMAIN ? process.env.VUE_APP_DOMAIN : '';
  const _apiUrl = _appDomain + process.env.VUE_APP_API_URL;
  import UploadWrapper from '../dialog/UploadWrapper.vue';
  import UploadDialog from '../dialog/UploadDialog.vue';
  import { mapGetters, mapActions } from 'vuex';
  import { PairingStatus } from '../../store/modules/upload/pairing-file.store';
  import FixDeleteBtn from '../button/FixDeleteBtn.vue';
  import FileDetailDialog from '../dialog/FileDetailDialog.vue';
  import { AIAnalyzeStatus } from '../../store/modules/defect/ai-analyze.store';
  import {
    createDefaultTableOptions,
    buildSortParam,
    buildPaginationParams,
    buildApiUrl,
    transformApiResponse
  } from '../../utils/tableUtils';
  import AIAnalyzeDialog from '../dialog/AIAnalyzeDialog.vue';
  import { getSortParam } from '../../utils/sortUtils';
  import { getTestId } from '@/utils/testUtils';
  import { normalizeReportInfo } from '../../utils/reportUtils';
  import { crudTablePageSizeFitMixin } from '@/ui/mixins/crudTablePageSizeFitMixin';

  const defaultEditedItem = {
    id: null,
    examineDate: '',
    reportDesc: '',
    name: '',
    inspectionCompanyName: '',
    inspectionDrone: '',
    inspectionCamera: '',
    inspectorName: '',
    inspectorPhoneNo: '',
    inspectorEmailAddr: '',
    irradiance: 0,
    cameraPitch: -90,
    thermalBundleFile: null,
    rgbBundleFile: null,
    locationBundleFile: null,
    thermalDataBundleFile: null,
    site: { text: '', value: null },
    examineHour: { text: '', value: null },
    aiAnalysisJob: {
      type: null,
      solarExamineReport: null,
      targetImageCount: null,
      processStatus: 'BEF',
      processRate: null,
      jobRefSeq: null
    }
  };

  export default {
    name: 'SolarExamineReportCrudTable',
    mixins: [crudTablePageSizeFitMixin],
    components: {
      UploadWrapper,
      UploadDialog,
      FileDetailDialog,
      AIAnalyzeDialog,
      FixDeleteBtn
    },
    data() {
      return {
        domain: 'solarExamineReports',
        // tableUtils를 사용한 기본 테이블 옵션 생성 (created에서 viewport fit로 덮어씀)
        tableOptions: createDefaultTableOptions(['id'], [true], 10),
        fileDetailDialog: false,
        uploadDialog: false,
        aIAnalyzeDialog: false,
        dialog: false,
        search: '',
        dialogMode: '',
        currentReportId: 0,
        mode: '',
        solarExamineReports: [],
        sites: [],
        editedItem: { ...defaultEditedItem },
        page: 1,
        paging: true,
        itemsPerPage: 10,
        pageCount: 0,
        PairingStatus: PairingStatus,
        AIAnalyzeStatus: AIAnalyzeStatus,
        apiLoading: true,
        currentAbortController: null,
        dialogKey: 0
      };
    },
    computed: {
      ...mapGetters({
        pairingStatus: 'pairingfile/pairingStatus'
      }),
      headers() {
        void this.$i18n.locale;
        return [
          { text: this.$t('inspection.tableReportName'), value: 'name' },
          { text: this.$t('inspection.tableSiteNameCol'), value: 'site.name' },
          { text: this.$t('inspection.tableExamineDate'), value: 'examineDate' },
          { text: this.$t('inspection.tableUploadedImages'), value: 'uploadedImageCounts', sortable: false },
          { text: this.$t('inspection.tableFileUpload'), value: 'fileUpload', sortable: false },
          { text: this.$t('inspection.tableImagePairing'), value: 'imagePairing', sortable: false },
          { text: this.$t('inspection.tableDefectAdd'), value: 'defectAdd', sortable: false },
          { text: this.$t('inspection.tableAiJob'), value: 'aIAnalyze', sortable: false },
          { text: this.$t('inspection.tableFileDetail'), value: 'fileDetail', sortable: false },
          { text: this.$t('inspection.editReports'), value: 'actions', sortable: false }
        ];
      },
      examineHours() {
        void this.$i18n.locale;
        return [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19].map(h => ({
          text:
            h <= 11
              ? this.$t('inspection.examineHourMorning', { hour: h })
              : h === 12
                ? this.$t('inspection.examineHourNoon')
                : this.$t('inspection.examineHourAfternoon', { hour: h - 12 }),
          value: h
        }));
      }
    },
    watch: {
      totalProgress(newValue) {
        if (newValue > 0 && newValue < 100) {
          // this.triggerSnackbar();
        } else if (newValue === 100) {
          setTimeout(() => {
            // this.hideSnackbar();
          }, 3000);
        }
      },
      aIAnalyzeDialog(newValue) {},
      search(val) {
        this.searchReportData(val, 1, this.tableOptions.sortBy, this.tableOptions.sortDesc);
      },
      page(val) {
        if (this.isCrudTableFitProgrammaticUpdate()) {
          return;
        }
        this.page = val;
        this.searchReportData(
          this.search,
          val,
          this.tableOptions.sortBy,
          this.tableOptions.sortDesc
        );
      }
    },
    methods: {
      ...mapActions({
        // triggerSnackbar: "uploadsnackbar/triggerSnackbar",
        // hideSnackbar: "uploadsnackbar/hideSnackbar",
        pairingFiles: 'pairingfile/pairingFiles'
      }),

      getTestId(testId) {
        return getTestId(testId);
      },

      formatUploadedImageCounts(item) {
        const rgb = item?.rgbImageCount ?? 0;
        const ir = item?.thermalImageCount ?? item?.imageCount ?? 0;
        return this.$t('inspection.uploadedImageCountsRgbIr', { rgb, ir });
      },
      hasUploadCountMismatch(item) {
        const rgb = Number(item?.rgbImageCount ?? 0);
        const ir = Number(item?.thermalImageCount ?? item?.imageCount ?? 0);
        return rgb !== ir;
      },
      uploadedImageCountsClass(item) {
        return this.hasUploadCountMismatch(item) ? 'warning--text font-weight-medium' : '';
      },

      getAIAnalyzeButtonConfig(item) {
        const statusMap = {
          BEF: { icon: 'mdi-robot-outline', label: this.$t('inspection.aiBtnRequest') },
          FIN: { icon: 'mdi-check-circle-outline', label: this.$t('inspection.aiBtnDone') },
          ING: { icon: 'mdi-progress-clock', label: this.$t('inspection.aiBtnProgress') },
          FLR: { icon: 'mdi-alert-circle-outline', label: this.$t('inspection.aiBtnFailed') },
          UNK: { icon: 'mdi-alert-circle-outline', label: this.$t('inspection.aiBtnUnknown') }
        };
        return statusMap[this.getAIAnalyzeStatus(item.id)] || {};
      },
      getAIAnalyzeStatus(reportId) {
        return (
          this.solarExamineReports.find(item => item.id === reportId)?.aiAnalysisJob
            ?.processStatus || 'BEF'
        );
      },
      isPairingRunning(item) {
        return this.pairingStatus(item.id) === PairingStatus.pairing;
      },
      isAiRunning(item) {
        return this.getAIAnalyzeStatus(item.id) === 'ING';
      },
      isUploadLocked(item) {
        return !!item.fileUpload;
      },
      isDefectLocked(item) {
        return !!item.defectAdd;
      },
      isReportRowWorkInProgress(item) {
        return (
          this.isPairingRunning(item) ||
          this.isAiRunning(item) ||
          this.isUploadLocked(item) ||
          this.isDefectLocked(item)
        );
      },
      aiAnalyzeButtonIcon(item) {
        const c = this.getAIAnalyzeButtonConfig(item);
        return c.icon || 'mdi-robot-outline';
      },
      aiAnalyzeButtonLabel(item) {
        const c = this.getAIAnalyzeButtonConfig(item);
        return c.label || this.$t('inspection.tableAiJob');
      },
      /**
       * 기존의 sort기능을 사용하지 않도록 처리
       */
      customSort(items, _index, _isDesc) {
        return items;
      },
      changedOptions(val) {
        // tableOptions는 이미 자동으로 업데이트됨
        // 별도로 변경할 필요 없음

        // 초기 로드 시에도 데이터를 가져오기 위해 searchReportData 호출
        this.searchReportData(
          this.search,
          this.page,
          this.tableOptions.sortBy,
          this.tableOptions.sortDesc
        );
      },

      /**
       * solarExamineReport 데이터 조회
       * @param keyword
       * @param sortBy (seq, createTime, solarExamineReport, processStatus )
       * @param sortDesc (sortDesc: true는 내림차순, false는 오름차순)
       * @param page
       */
      async searchReportData(keyword, page, sortBy, sortDesc) {
        const request = this.beginCrudTableRequest();
        const controller = request.controller;
        try {
          this.currentAbortController = controller;
          this.apiLoading = true;

          // 정렬 파라미터 생성 - ProjectCrudTable과 동일한 방식
          let sortParam = '';
          if (sortBy && sortBy.length > 0) {
            const sortableFields = {
              name: 'reportName',
              'site.name': 'site.name',
              examineDate: 'examineDate',
              id: 'id'
            };

            const sortFields = sortBy.map((field, index) => {
              const direction = sortDesc && sortDesc[index] ? 'desc' : 'asc';
              const apiField = sortableFields[field] || field;
              return `${apiField},${direction}`;
            });
            sortParam = sortFields.join(',');
          } else {
            // 기본값: id,desc
            sortParam = 'id,desc';
          }

          // 페이지 번호는 1부터 시작하도록 조정
          const currentPage = page || this.page;
          const adjustedPage = currentPage - 1; // API는 0부터 시작

          console.log('sortBy:', sortBy);
          console.log('sortDesc:', sortDesc);

          //현재열려있는 모달의 reportId의 서버에서 받은 보고서 데이터로 새로고침
          // AbortController 지원을 위해 axios 직접 사용 - 가독성 개선: API 호출 바로 위에서 params 구성
          const [reportRes, siteRes] = await Promise.all([
            // 보고서 데이터 조회
            axios({
              method: 'get',
              url: _apiUrl + '/examine-report',
              headers: { Authorization: authHeader() },
              params: {
                size: this.itemsPerPage,
                page: adjustedPage,
                sort: sortParam,
                keyword: keyword || '' // keyword가 없어도 빈 문자열로 항상 전달
              },
              signal: controller.signal
            }),
            // 사이트 목록 조회
            axios({
              method: 'get',
              url: _apiUrl + '/sites/list',
              headers: { Authorization: authHeader() },
              params: {},
              signal: controller.signal
            })
          ]);
          if (!this.isCrudTableRequestCurrent(request.token)) return;

          const transformedData = transformApiResponse(reportRes.data, items => {
            return items.map(item => {
              // 원본 데이터 구조 유지 (기존 호환성)
              const rawItem = {
                aiAnalysisJob: item?.aiAnalysisJob,
                aiAnalyzed: item?.aiAnalyzed,
                examineDate: item?.examineDate,
                id: item?.id,
                reportDesc: item?.reportDesc,
                name: item?.reportName,
                site: item?.site,
                imageCount: item?.imageCount || 0,
                rgbImageCount: item?.rgbImageCount ?? 0,
                thermalImageCount: item?.thermalImageCount ?? item?.imageCount ?? 0,
                cameraPitch: item?.cameraPitch
              };

              // 정규화된 보고서 정보 추가 (dialog에서 사용)
              const reportInfo = normalizeReportInfo(
                {
                  name: item?.reportName,
                  site: item?.site,
                  examineDate: item?.examineDate
                },
                'table'
              );

              return {
                ...rawItem,
                reportInfo // 정규화된 보고서 정보
              };
            });
          });

          this.solarExamineReports = transformedData.items;
          this.pageCount = transformedData.pageCount;
          this.page = transformedData.page;

          //사이트 목록 가져옴
          // console.log("사이트 목록", siteRes.data);
          if (siteRes.data?.content) {
            this.sites = siteRes.data.content;
          }
          //현재 리포트의 상세모달정보 가져옴
          if (this.currentReportId) {
            this.solarExamineReports.find(item => {
              if (item.id === this.currentReportId) {
                this.getReportDetail(item);
              }
            });
          }
        } catch (e) {
          if (!this.isCrudTableRequestCurrent(request.token)) return;
          console.log('에러', e);
        } finally {
          if (this.isCrudTableRequestCurrent(request.token)) {
            this.currentAbortController = null; // 최신 요청만 초기화
            this.apiLoading = false;
            this.finishCrudTableRequest(request.token);
          }
        }
      },
      initialize() {
        this.searchReportData();
      },
      onFine(reportId, aiAnalysis) {
        this.editedItem.aiAnalysisJob = aiAnalysis;
        this.$set(this.editedItem, 'aiAnalysisJob', aiAnalysis);
        this.initialize();
      },
      //상세 모달 api
      async getReportDetail(item) {
        this.apiLoading = true;
        try {
          const res = await Api.read(`/solarExamineReports/${item.id}`);
          const source = res?.data;

          // 개별 보고서 조회 API에는 site 정보가 없으므로, 테이블의 item.site 정보를 source에 추가
          if (item?.site && !source?._embedded?.site) {
            if (!source._embedded) {
              source._embedded = {};
            }
            source._embedded.site = item.site;
          }

          //지정한 editedItem의 항목과 일치하는 source값만 받아오도록 처리 + site,examineHour은 combobox의 데이터 구조에 맞게 변경

          await this.updateObject(this.editedItem, source, item);
        } catch (e) {
          console.log('에러', e);
        } finally {
          this.apiLoading = false;
        }
      },
      async updateObject(target, source, item) {
        this.currentReportId = item.id;
        this.mode = 'edit';
        for (const key in target) {
          if (typeof target[key] === 'object' && target[key] !== null && source[key] !== null) {
            await this.setObject(target, key, source, item);
          } else {
            if (source.hasOwnProperty.call(source, key)) {
              await this.$set(target, key, source[key]);
            }
          }
        }
      },
      /**
       * 객체형태의 데이터 할당
       * targetKey: 할당할 key, source: 할당할 데이터
       * */
      async setObject(target, key, source, item) {
        //editedItem(target) <= source
        //site:{text,value} <= source.site:{name,id}
        //examineHour:{text,value} <= source.examineHour에 examineHours의 값 중에 value가 같은 값을 할당
        if (key === 'site' || key === 'examineHour') {
          if (key === 'site') {
            this.$set(target, key, {
              text: source['_embedded']['site']?.name ?? '',
              value: source['_embedded']['site']?.id ?? 0
            });
          }
          if (key === 'examineHour') {
            //examineHours객체에서 해당 examineHour 값에 해당하는 객체를 찾아서 넣어준다.
            const findExamineHour =
              this.examineHours.find(item => item.value === source[key]) ?? null;
            if (findExamineHour) {
              this.$set(target, key, findExamineHour);
            }
          }
        } else {
          await this.updateObject(target[key], source, item);
        }
      },
      async editItem(item) {
        this.apiLoading = true;
        try {
          await this.getReportDetail(item);
          this.dialog = true;
        } catch (e) {
          console.log('에러', e);
        } finally {
          this.apiLoading = false;
        }
      },
      deleteItem(item) {
        this.apiLoading = true;
        try {
          const index = this.solarExamineReports.indexOf(item);
          if (confirm(this.$t('common.confirmDeleteSelected'))) {
            Api.examineReport.delete(`/api/examine-report/${item.id}`).then(() => {
              this.solarExamineReports.splice(index, 1);
              this.initialize();
            });
          }
        } catch (e) {
          console.log('에러', e);
        } finally {
          this.apiLoading = false;
        }
      },
      closeDialog() {
        this.dialog = false;
        this.initDialog();
      },
      /**
       * Dialog 관련 상태 초기화
       * editedItem, currentReportId 초기화 및 dialogKey 증가
       */
      initDialog() {
        Object.assign(this.editedItem, { ...defaultEditedItem });
        this.currentReportId = null;
        this.dialogKey++;
      },
      onSave(reportId) {
        this.currentReportId = reportId;
        this.initialize();
        this.closeDialog();
      },
      onInitialize() {
        console.log('onInitialize');
        this.initialize();
      },
      onError(message) {
        alert(message);
      },
      next() {
        this.page++;
        this.initialize();
      },
      previous() {
        this.page--;
        this.initialize();
      },
      paginationInput(event) {
        this.page = event;
        this.initialize();
      },
      async openDialog(item, modeValue) {
        if (item?.id === null) {
          await Object.assign(this.editedItem, item);
          this.currentReportId = item.id;
        } else {
          this.currentReportId = 0;
        }
        this.mode = modeValue;
        this.dialogKey++;
        this.dialog = true;
      },
      async openUploadDialog(item, modeValue) {
        await Object.assign(this.editedItem, item);
        this.currentReportId = item.id;
        this.uploadDialog = true;
        this.mode = modeValue;
        this.dialogKey++;
      },
      closeUploadDialog() {
        this.uploadDialog = false;
        this.initDialog();
      },
      async openAIAnalyzeDialog(item) {
        await Object.assign(this.editedItem, item);
        this.currentReportId = item.id;
        this.aIAnalyzeDialog = true;
        this.dialogKey++;
      },
      closeAIAnalyzeDialog() {
        this.aIAnalyzeDialog = false;
        this.initDialog();
      },
      async openFileDialog(item, modeValue) {
        await Object.assign(this.editedItem, item);
        this.currentReportId = item.id;
        this.fileDetailDialog = true;
        this.mode = modeValue;
        this.dialogKey++;
      },
      closeFileDialog() {
        this.fileDetailDialog = false;
        this.initDialog();
      },
      /**
       * 페어링 시작
       * @param {*} reportId
       * 설명 : 페어링 중에는 loading중으로 처리
       * 해당 페어링 버튼만 loading형태로처리하는게 best고 다른 페이지 이동해도 되는게 제일 좋긴해
       */
      startPairing(item) {
        //페어링 시작
        this.apiLoading = true;
        try {
          this.pairingFiles({ reportId: item.id });
        } catch (e) {
          console.log('에러', e);
        } finally {
          this.apiLoading = false;
        }
        //해당 report의 rgb, ir,therm이 업로드 되고있으면 현재 업로드가 진행중입니다. 현재 업로드 완료된 파일만을 가지고 페어링을 시작하시겠습니까?
      }
    },
    mounted() {
      // 초기 정렬 옵션을 명시적으로 설정
      this.tableOptions.sortBy = ['id'];
      this.tableOptions.sortDesc = [true];

      // changedOptions에서 자동으로 초기 데이터 로딩이 진행되므로 별도 호출 불필요
    }
  };
</script>

<style scoped>
  .hover-underline {
    color: inherit;
    text-transform: none;
  }

  .hover-underline:hover {
    text-decoration: underline;
  }
  .v-data-table row col {
    padding: 0 !important;
  }
</style>
